/**
 * SYNCFUSION GRID TEST COMPONENT
 * 
 * This component tests if Syncfusion Grid has been set up correctly
 * 
 * Usage:
 * 1. Import this component into App.tsx or any route
 * 2. Run dev server: npm run dev
 * 3. Navigate to the page with this component
 * 4. Verify the grid displays correctly
 * 
 * Expected results:
 * - Grid displays 10 rows of data
 * - Pagination working (5 items per page)
 * - Sorting working (click column header)
 * - Filtering working (click filter icon)
 * - Search working (type in search box)
 * 
 * Created: October 2025
 */

import React from 'react';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Sort,
  Filter,
  Toolbar,
} from '@syncfusion/ej2-react-grids';

interface TestData {
  id: number;
  name: string;
  email: string;
  status: string;
  age: number;
}

const SyncfusionGridTest: React.FC = () => {
  // ========== MOCK DATA ==========
  const testData: TestData[] = [
    { id: 1, name: 'John Doe', email: 'john@example.com', status: 'Active', age: 28 },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', status: 'Active', age: 32 },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', status: 'Inactive', age: 45 },
    { id: 4, name: 'Alice Brown', email: 'alice@example.com', status: 'Active', age: 24 },
    { id: 5, name: 'Charlie Wilson', email: 'charlie@example.com', status: 'Pending', age: 35 },
    { id: 6, name: 'Diana Moore', email: 'diana@example.com', status: 'Active', age: 29 },
    { id: 7, name: 'Edward Taylor', email: 'edward@example.com', status: 'Inactive', age: 41 },
    { id: 8, name: 'Fiona Anderson', email: 'fiona@example.com', status: 'Active', age: 27 },
    { id: 9, name: 'George Thomas', email: 'george@example.com', status: 'Pending', age: 38 },
    { id: 10, name: 'Hannah Jackson', email: 'hannah@example.com', status: 'Active', age: 31 },
  ];

  // ========== GRID CONFIGURATION ==========
  const pageSettings = { pageSize: 5 };

  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 600, marginBottom: '10px' }}>
          🧪 Syncfusion Grid Test Component
        </h1>
        <p style={{ color: '#666', fontSize: '16px' }}>
          This component verifies that Syncfusion Grid has been set up correctly
        </p>
      </div>

      {/* Test Grid */}
      <div style={{ 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <GridComponent
          dataSource={testData}
          allowPaging={true}
          pageSettings={pageSettings}
          allowSorting={true}
          allowFiltering={true}
          filterSettings={{ type: 'Menu' }}
          toolbar={['Search']}
        >
          <ColumnsDirective>
            <ColumnDirective 
              field='id' 
              headerText='ID' 
              width='80' 
              textAlign='Center' 
              isPrimaryKey={true}
            />
            <ColumnDirective 
              field='name' 
              headerText='Name' 
              width='150' 
            />
            <ColumnDirective 
              field='email' 
              headerText='Email' 
              width='200' 
            />
            <ColumnDirective 
              field='status' 
              headerText='Status' 
              width='120' 
            />
            <ColumnDirective 
              field='age' 
              headerText='Age' 
              width='80' 
              textAlign='Center' 
            />
          </ColumnsDirective>

          <Inject services={[Page, Sort, Filter, Toolbar]} />
        </GridComponent>
      </div>

      {/* Success Criteria */}
      <div style={{ 
        marginTop: '20px', 
        padding: '20px', 
        background: '#f5f5f5', 
        borderRadius: '8px' 
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '15px' }}>
          🎯 Success Criteria:
        </h3>
        <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
          <li>✅ Grid displays 10 rows of data correctly</li>
          <li>✅ Pagination: 2 pages (5 items/page)</li>
          <li>✅ Sorting: Click header changes sort order</li>
          <li>✅ Filtering: Filter menu displays correctly</li>
          <li>✅ Search: Search box is working</li>
          <li>✅ No console errors about license key</li>
        </ul>
      </div>
    </div>
  );
};

export default SyncfusionGridTest;
